import { FX_PAIRS, normalizeOptionLabels, ZH_LABEL_LISTS, type SettingsDto, type SettingsUpdateInput, type ZhLabelList } from "@hisense/shared";

/** Danh mục có cột tên tiếng Hoa (`optionLabels.ts`) — hiện là loại sản phẩm. */
export const isZhLabelList = (key: string): key is ZhLabelList => (ZH_LABEL_LISTS as readonly string[]).includes(key);
export type SettingsSection =
  "company" | "defaults" | "fx" | "output" | `list:${string}`;
export function sectionPayload(
  draft: SettingsDto,
  section: SettingsSection,
): SettingsUpdateInput {
  if (section === "company") return { companyInfo: draft.companyInfo };
  if (section === "defaults") {
    const { defaultCurrency, defaultProductUnit, language } = draft.system;
    return { system: { defaultCurrency, defaultProductUnit, language } };
  }
  // Tiền tệ theo công ty + tỷ giá (yêu cầu v1.8 mục 6.2, 6.3): một biểu mẫu, gửi đủ sáu cặp — cặp để trống là bỏ tỷ giá.
  if (section === "fx")
    return {
      system: { defaultCurrencyByRegion: { ...draft.system.defaultCurrencyByRegion } },
      businessOperations: {
        fxRates: FX_PAIRS.map(([base, quote]) => ({
          base,
          quote,
          rate: draft.businessOperations.fxRates.find((r) => r.base === base && r.quote === quote)?.rate ?? "",
        })),
      },
    };
  if (section === "output") return { labelPrinter: draft.labelPrinter };
  const key = section.slice(5);
  const values = draft.businessOperations.optionLists[key] ?? [];
  return {
    businessOperations: {
      optionLists: { [key]: values },
      // Tên tiếng Hoa lưu cùng lượt với danh mục của nó; chỉ giữ dòng còn trong danh mục và có chữ.
      ...(isZhLabelList(key)
        ? { optionLabelsZh: { [key]: normalizeOptionLabels(draft.businessOperations.optionLabelsZh?.[key], values) } }
        : {}),
    },
  };
}
export function sectionDirty(
  draft: SettingsDto,
  base: SettingsDto,
  section: SettingsSection,
) {
  return (
    JSON.stringify(sectionPayload(draft, section)) !==
    JSON.stringify(sectionPayload(base, section))
  );
}
export function replaceSection(
  draft: SettingsDto,
  source: SettingsDto,
  section: SettingsSection,
): SettingsDto {
  if (section === "company")
    return { ...draft, companyInfo: { ...source.companyInfo } };
  if (section === "defaults")
    return { ...draft, system: { ...source.system, defaultCurrencyByRegion: { ...draft.system.defaultCurrencyByRegion } } };
  if (section === "fx")
    return {
      ...draft,
      system: { ...draft.system, defaultCurrencyByRegion: { ...source.system.defaultCurrencyByRegion } },
      businessOperations: { ...draft.businessOperations, fxRates: source.businessOperations.fxRates.map((r) => ({ ...r })) },
    };
  if (section === "output")
    return { ...draft, labelPrinter: { ...source.labelPrinter } };
  const key = section.slice(5);
  return {
    ...draft,
    businessOperations: {
      ...draft.businessOperations,
      optionLists: {
        ...draft.businessOperations.optionLists,
        [key]: [...(source.businessOperations.optionLists[key] ?? [])],
      },
      optionLabelsZh: {
        ...draft.businessOperations.optionLabelsZh,
        [key]: { ...source.businessOperations.optionLabelsZh?.[key] },
      },
    },
  };
}
export function formatCounter(c: {
  prefix: string | null;
  pad: number | null;
  nextValue: number;
}) {
  return `${c.prefix ?? ""}${String(c.nextValue).padStart(c.pad ?? 0, "0")}`;
}
